<template>
  <CustomModal
    :key="selectedAccommodationId"
    class="popular-filters-edit-modal"
    :is-modal-open="isOpen"
    @toggleModal="closeEditingFn()"
    @onClickAway="closeEditingFn()"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    :custom-classes="'tw:w-[100vw] tw:md:max-w-[500px] tw:md:w-[500px] tw:md:rounded-md tw:rounded-none'"
  >
    <template #modalHeader>
      <p class="tw:text-xl tw:text-gray-700">{{ $t('top_amenities_header') }}</p>
    </template>

    <div v-if="!isPopularFiltersLoading" class="tw:flex tw:flex-wrap">
      <div v-for="field in selectablePopularFilters" :key="field.featureId" class="feature-field tw:my-2">
        <Checkbox
          :name="field.featureId"
          :label="field.name"
          :value="getCurrentValue(field)"
          class="tw:text-gray-800"
          @onChange="checked => toggleCheckbox(checked, field.featureId, field.studioGroupId)"
        />
      </div>
    </div>
    <LaunchpadLoader
      v-else
      class="tw:min-h-[200px] tw:flex tw:items-center tw:w-[100vw] tw:md:w-[500px] tw:justify-center"
    />

    <template #modalFooter>
      <div class="tw:w-full tw:flex tw:justify-end tw:gap-4">
        <GhostButton size="medium" @click="closeEditingFn()">{{ $t('close') }}</GhostButton>
        <MainButton size="medium" @click="savePropertyFeatures">
          <span v-if="!saveFeaturesLoading">{{ $t('save') }}</span>
          <span v-if="saveFeaturesLoading">
            <SmallDotLoader color="#fff" />
          </span>
        </MainButton>
      </div>
    </template>
  </CustomModal>
</template>
<script setup>
import { computed, onMounted, ref } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'

import CustomModal from '@/components/base/CustomModal.vue'
import Checkbox from '@/components/base/inputs/Checkbox.vue'
import LaunchpadLoader from '@/components/dashboard/common/LaunchpadLoader.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import useToastNotifications from '@/components/notifications/useToastNotifications'
import apis from '@/services'

import { useGetPopularFilters } from '@/components/propertyDetails/queries'
import { popularFiltersIds } from '@/constants/propertyDetailsPopularFilters'
import { selectedAccommodationId } from '@/layouts/queries'

const queryClient = useQueryClient()
const props = defineProps({
  isOpen: Boolean,
  currentPopularFilters: Array,
  allCurrentFilters: Array,
})
const emit = defineEmits(['closeEditing'])
const closeEditingFn = () => emit('closeEditing')
const saveFeaturesLoading = ref(false)

const { allPopularFilters, isPopularFiltersLoading } = useGetPopularFilters()
const selectablePopularFilters = computed(() => {
  return allPopularFilters.value?.filter(item => popularFiltersIds.includes(item.featureId))
})
const valuesToUpdate = ref([])

onMounted(() => {
  const grouped = props.currentPopularFilters.reduce((acc, { featureId, studioGroupId }) => {
    let group = acc.find(item => Number(item.fieldsGroup) === Number(studioGroupId))
    if (!group) {
      group = { fieldsGroup: studioGroupId, fields: [] }
      acc.push(group)
    }

    group.fields.push({ featureId, fieldsGroup: studioGroupId })

    return acc
  }, [])

  valuesToUpdate.value = grouped
})

const { displayNotification } = useToastNotifications()

const toggleCheckbox = (checked, fieldId, groupId) => {
  const newFields = getFields(groupId, fieldId, checked)
  const groupIndex = valuesToUpdate.value.findIndex(item => item.fieldsGroup === groupId)

  if (groupIndex < 0) {
    valuesToUpdate.value.push({
      fieldsGroup: groupId,
      fields: newFields,
    })
  } else if (groupIndex >= 0) {
    valuesToUpdate.value[groupIndex].fields = newFields
  }
}

const getFields = (groupId, fieldId, checked) => {
  const allFilters = [...valuesToUpdate.value]
  let fields = allFilters.find(item => item.fieldsGroup === groupId)?.fields ?? []
  const fieldIndex = fields.findIndex(item => Number(item.featureId) === Number(fieldId))
  if (fieldIndex >= 0 && !checked) {
    // Field exists and is not checked, remove it from fields.
    fields = fields.filter(item => Number(item.featureId) !== Number(fieldId))
  } else if (fieldIndex < 0 && checked) {
    // Field does not exist and is checked, add it to fields.
    fields = [...fields, { featureId: fieldId, studioGroupId: groupId }]
  }

  return fields
}
const getCurrentValue = field => {
  let checkboxValue = false
  if (props.currentPopularFilters?.length > 0) {
    props.currentPopularFilters.forEach(item => {
      if (item.featureId === field.featureId) {
        checkboxValue = true
      }
    })
  }
  return checkboxValue
}
const savePropertyFeatures = () => {
  saveFeaturesLoading.value = true
  const promises = []
  valuesToUpdate.value.forEach(item => {
    promises.push(
      new Promise((resolve, reject) => {
        apis.accommodation
          .updatePropertyFeatures(selectedAccommodationId.value, item.fieldsGroup, item.fields)
          .then(res => resolve(res))
          .catch(() => reject())
      })
    )
  })

  Promise.all(promises)
    .then(() => {
      queryClient.invalidateQueries({ queryKey: ['propertyFeatures'] })
      valuesToUpdate.value = []
      displayNotification({
        message: 'app_hotel_details_successfully_saved',
        isTranslationKey: true,
        isHtml: true,
        type: 'success',
      })
      saveFeaturesLoading.value = false
      closeEditingFn()
    })
    .catch(() => {
      valuesToUpdate.value = []
      displayNotification({
        message: 'something_went_wrong',
        isTranslationKey: true,
        type: 'error',
      })
      saveFeaturesLoading.value = false
      closeEditingFn()
    })
}
</script>
<style scoped>
.feature-field {
  flex: 50% 0 0;
  @media (max-width: 768px) {
    flex: 100% 0 0;
  }
}
</style>
